import { useId, useMemo, useRef, useState, type ClipboardEvent, type KeyboardEvent } from 'react';
import { Icon } from './icons';
import './tag-input.css';

/**
 * Champ d'étiquettes à pastilles (design system, collection « Saisie ») : chaque étiquette est une
 * pastille retirable ; Entrée, virgule, point-virgule ou Tab la valident ; un collage « a, b; c »
 * en crée plusieurs ; Retour arrière sur un champ vide retire la dernière. Les suggestions — les
 * étiquettes déjà posées dans le même vocabulaire, avec leur nombre d'emplois — se proposent en
 * liste déroulante (combobox ARIA, flèches et Entrée) ; une saisie qui ne diffère d'une étiquette
 * connue que par la casse ou les accents reprend l'orthographe connue (pas de « Terrine » à côté de
 * « terrine »). Le composant ne valide rien : le serveur reste juge.
 */

export interface TagSuggestion {
  tag: string;
  count?: number;
}

const fold = (value: string): string => value.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
const SEPARATORS = /[,;\n]/;

export function TagInput({
  label,
  value,
  onChange,
  suggestions = [],
  placeholder = 'Ajouter une étiquette…',
  maxLength = 60,
}: {
  label: string;
  value: readonly string[];
  onChange: (tags: string[]) => void;
  suggestions?: readonly TagSuggestion[];
  placeholder?: string;
  maxLength?: number;
}) {
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  const chosen = useMemo(() => new Set(value.map(fold)), [value]);
  const known = useMemo(() => new Map(suggestions.map((entry) => [fold(entry.tag), entry.tag])), [suggestions]);
  // Propositions, puis « Créer « … » » quand la saisie n'est ni connue ni déjà choisie.
  const options = useMemo(() => {
    const typed = draft.trim();
    const wanted = fold(typed);
    const matches: Array<TagSuggestion & { create?: boolean }> = suggestions.filter((entry) => !chosen.has(fold(entry.tag)) && (wanted === '' || fold(entry.tag).includes(wanted))).slice(0, 8);
    if (typed !== '' && !known.has(wanted) && !chosen.has(wanted)) matches.push({ tag: typed, create: true });
    return matches;
  }, [suggestions, chosen, known, draft]);

  const add = (raw: readonly string[]): void => {
    const next = [...value];
    const seen = new Set(next.map(fold));
    for (const piece of raw) {
      const trimmed = piece.trim().slice(0, maxLength);
      if (trimmed === '') continue;
      const spelled = known.get(fold(trimmed)) ?? trimmed;
      if (seen.has(fold(spelled))) continue;
      seen.add(fold(spelled));
      next.push(spelled);
    }
    if (next.length !== value.length) onChange(next);
    setDraft('');
    setActive(0);
  };
  const remove = (tag: string): void => {
    onChange(value.filter((entry) => entry !== tag));
    input.current?.focus();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    const highlighted = options.length > 0 ? options[Math.min(active, options.length - 1)] : undefined;
    if (event.key === 'ArrowDown' && options.length > 0) {
      event.preventDefault();
      setOpen(true);
      setActive((current) => (current + 1) % options.length);
    } else if (event.key === 'ArrowUp' && options.length > 0) {
      event.preventDefault();
      setOpen(true);
      setActive((current) => (current - 1 + options.length) % options.length);
    } else if (event.key === 'Enter') {
      // Entrée choisit l'élément surligné (une étiquette connue, ou « Créer »), sans soumettre le formulaire.
      if (!open || highlighted === undefined) return;
      event.preventDefault();
      add([highlighted.tag]);
    } else if ((event.key === ',' || event.key === ';' || event.key === 'Tab') && draft.trim() !== '') {
      // Virgule, point-virgule ou Tab : la saisie telle quelle (orthographe connue reprise).
      event.preventDefault();
      add([draft]);
    } else if (event.key === 'Backspace' && draft === '' && value.length > 0) {
      onChange(value.slice(0, -1));
    } else if (event.key === 'Escape' && open) {
      event.preventDefault();
      setOpen(false);
    }
  };
  const onPaste = (event: ClipboardEvent<HTMLInputElement>): void => {
    const text = event.clipboardData.getData('text');
    if (!SEPARATORS.test(text)) return;
    event.preventDefault();
    add(text.split(SEPARATORS));
  };

  const listId = `${id}-list`;
  const showList = open && options.length > 0;
  return (
    <div className="ay-field ay-taginput">
      <label className="ay-field__label" htmlFor={id}>{label}</label>
      <div className="ay-taginput__box" onClick={() => input.current?.focus()}>
        <ul className="ay-taginput__chips" aria-label={`${label} choisies`}>
          {value.map((tag) => (
            <li key={tag} className="ay-taginput__chip">
              <span>{tag}</span>
              <button type="button" aria-label={`Retirer ${tag}`} onClick={(event) => { event.stopPropagation(); remove(tag); }}>
                <Icon name="close" size={12} />
              </button>
            </li>
          ))}
        </ul>
        <input
          ref={input}
          id={id}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={showList}
          aria-controls={listId}
          aria-activedescendant={showList ? `${id}-option-${Math.min(active, options.length - 1)}` : undefined}
          value={draft}
          maxLength={maxLength}
          placeholder={value.length === 0 ? placeholder : ''}
          onChange={(event) => {
            const text = event.target.value;
            if (SEPARATORS.test(text)) add(text.split(SEPARATORS));
            else {
              setDraft(text);
              setActive(0);
              setOpen(true);
            }
          }}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            // La saisie en cours n'est pas perdue en quittant le champ.
            if (draft.trim() !== '') add([draft]);
            setOpen(false);
          }}
        />
      </div>
      {showList && (
        <ul className="ay-taginput__list" id={listId} role="listbox" aria-label={`Étiquettes proposées pour ${label}`}>
          {options.map((option, index) => (
            <li
              key={option.tag}
              id={`${id}-option-${index}`}
              role="option"
              aria-selected={index === Math.min(active, options.length - 1)}
              className="ay-taginput__option"
              onMouseDown={(event) => {
                // Avant le blur du champ : le clic choisit, il ne valide pas la saisie partielle.
                event.preventDefault();
                add([option.tag]);
              }}
              onMouseEnter={() => setActive(index)}
            >
              <Icon name={option.create === true ? 'plus' : 'tag'} size={13} />
              <span className="ay-taginput__option-tag">{option.create === true ? <>Créer « <strong>{option.tag}</strong> »</> : option.tag}</span>
              {option.count !== undefined && <span className="ay-taginput__option-count">{option.count.toLocaleString('fr')}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
